import { useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';
import { Suspense, useEffect, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Navigate, Route, Routes, useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { DialogLauncher } from '@formstack/phoenix/components/DialogLauncher';
import { MaterialThemeProvider } from '@formstack/phoenix/components/MaterialThemeProvider/MaterialThemeProvider';
import { StickyProvider } from '@formstack/phoenix/components/sticky';

import { AppLoader } from '@/common/components/AppLoader';
import { AppRouter } from '@/common/components/AppRouter';
import { ErrorBoundary } from '@/common/components/ErrorBoundary';
import { FeatureFlags } from '@/common/components/FeatureFlags/FeatureFlags';
import { GlobalStyle } from '@/common/components/GlobalStyle';
import { Toast } from '@/common/components/Toast';
import {
  HOME_PATH,
  PATH_QUERY_PARAMETER_NAMES,
  SETTINGS_PATH,
  TEMPLATE_PATH,
  WORKFLOWS_PATH,
  WORKSPACE_PATH,
} from '@/common/constants';
import { isAuthError } from '@/common/http/utils';
import { ResourceProvider, useResource, withResources } from '@/common/resources';
import { DocTitle } from '@/common/utils/DocTitleUtils';
import lazyWithRetry from '@/common/utils/lazyWithRetry';
import { withNestedRoutes } from '@/common/utils/route-helpers';
import { Masthead } from '@/components/Masthead';
import { AppStoreProvider } from '@/store/app.store';
import { isUserParticipant, useGetUserInfoQuery, userHasAccessTo } from '@/store/auth';
import { useToastNotification } from '@/store/common/toast';

import { SettingsView } from '../../views/settings';

const Workspace = lazyWithRetry(() => import('../../views/workspace'));
const WorkflowsView = lazyWithRetry(() => import('../../views/workflows'));
const HomeView = lazyWithRetry(() => import('../../views/home'));
const TemplateView = lazyWithRetry(() => import('../../views/template'));

const AppContainer = styled.div`
  width: 100vw;
  height: 100vh;

  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  z-index: 999;
  flex: 0 1 auto;
`;

const Body = styled.div`
  flex: 1 1 auto;
  overflow: auto;
`;

const MainApp = () => {
  const isTemplate = useMatch({ path: TEMPLATE_PATH, end: false });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { isParticipant, hasWorkspaceAccess } = useGetUserInfoQuery(undefined, {
    selectFromResult: ({ data: userInfo }) => ({
      isParticipant: isUserParticipant(userInfo),
      hasWorkspaceAccess: userHasAccessTo('workspace', userInfo),
    }),
  });

  const stickyRef = useRef(null);

  useEffect(() => {
    if (searchParams.has(PATH_QUERY_PARAMETER_NAMES.TEMPLATE_ID)) {
      navigate(`${TEMPLATE_PATH}?id=${searchParams.get(PATH_QUERY_PARAMETER_NAMES.TEMPLATE_ID)}`);
    }
  }, [navigate, searchParams]);

  return (
    <>
      <DocTitle>Workflows</DocTitle>
      <DialogLauncher />
      {isTemplate ? (
        <AppContainer>
          <Suspense fallback={<AppLoader />}>
            <Routes>
              <Route path={withNestedRoutes(TEMPLATE_PATH)} element={<TemplateView />} />
            </Routes>
          </Suspense>
        </AppContainer>
      ) : (
        <StickyProvider value={{ ref: stickyRef }}>
          <AppContainer ref={stickyRef}>
            <Header>
              <Masthead />
            </Header>
            <Body>
              <Suspense fallback={<AppLoader />}>
                {isParticipant ? (
                  <Routes>
                    {hasWorkspaceAccess && (
                      <Route path={withNestedRoutes(WORKSPACE_PATH)} element={<Workspace />} />
                    )}
                    <Route index element={<Navigate to={WORKSPACE_PATH} replace />} />
                  </Routes>
                ) : (
                  <Routes>
                    <Route path={withNestedRoutes(SETTINGS_PATH)} element={<SettingsView />} />
                    <Route path={withNestedRoutes(HOME_PATH)} element={<HomeView />} />
                    <Route path={withNestedRoutes(WORKFLOWS_PATH)} element={<WorkflowsView />} />
                    {hasWorkspaceAccess && (
                      <Route path={withNestedRoutes(WORKSPACE_PATH)} element={<Workspace />} />
                    )}
                    <Route index element={<Navigate to={HOME_PATH} replace />} />
                  </Routes>
                )}
              </Suspense>
            </Body>
          </AppContainer>
        </StickyProvider>
      )}
    </>
  );
};

const LDInitializer = withResources(({ resource }) => {
  const [loading, setLoading] = useState(true);
  const ldClient = useLDClient();
  useEffect(() => {
    if (ldClient) {
      const waitForInitialization = async () => {
        try {
          await ldClient.waitForInitialization();
          setLoading(false);
        } catch {
          // continue if LaunchDarkly fails to load
          setLoading(false);
        }
      };
      waitForInitialization();
    }
  }, [ldClient, resource]);
  return loading ? (
    <AppContainer>
      <AppLoader />
    </AppContainer>
  ) : (
    <FeatureFlags>
      <MainApp />
    </FeatureFlags>
  );
});

const LDProvider = withLDProvider({
  clientSideID: process.env.LAUNCH_DARKLY?.['clientSideID'] ?? '',
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
})(LDInitializer);

const AppContent = () => {
  const resource = useResource((resource) => ({
    errorDescription: resource.anErrorHasOccurred,
  }));
  const { data: userInfo, error } = useGetUserInfoQuery();
  const { showErrorNotification } = useToastNotification();

  useEffect(() => {
    if (error && !('status' in error && isAuthError(error?.status))) {
      showErrorNotification(resource.errorDescription);
    }
  }, [error, resource, showErrorNotification]);

  return !userInfo ? (
    <AppContainer>
      <AppLoader />
    </AppContainer>
  ) : (
    <LDProvider />
  );
};

const App = () => (
  <>
    <GlobalStyle />
    <AppRouter>
      <AppStoreProvider>
        <Toast />
        <ErrorBoundary>
          <DndProvider backend={HTML5Backend}>
            <MaterialThemeProvider>
              <ResourceProvider lang="en">
                <AppContent />
              </ResourceProvider>
            </MaterialThemeProvider>
          </DndProvider>
        </ErrorBoundary>
      </AppStoreProvider>
    </AppRouter>
  </>
);

export { App };
